<script setup>
/**
 * GviaSignatureModal
 * Full-screen modal for capturing client signature after payment allocation.
 * Shows payment summary + signature pad. "סיים" button is disabled until signed.
 * On confirm: saves wallet transactions, uploads signature, sends email, navigates home.
 */
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import SignaturePad from '@/ui/components/common/SignaturePad.vue'
import { useGvia } from '@/core/composables/useGvia'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { useBackButtonClose } from '@/core/composables/useBackButtonClose'
import { ROUTES } from '@/core/config/constants'
import { getWalletAgentId } from '@/core/services/wallet'

const props = defineProps({
  allocation: { type: Object, required: true },
})

const emit = defineEmits(['close'])

const router = useRouter()
const authStore = useAuthStore()
const { success: toastSuccess, error: toastError } = useToast()
const { clientData, confirmPayment, reset, formatCurrency } = useGvia()

const isOpen = ref(true)
useBackButtonClose(isOpen, () => emit('close'))

const signaturePadRef = ref(null)
const hasSignature = ref(false)
const saving = ref(false)

const hasCash = computed(() => (props.allocation.cash || 0) > 0)
const hasChecks = computed(() => (props.allocation.checks || 0) > 0)

const canFinish = computed(() => hasSignature.value && !saving.value)

function handleSignatureUpdate(hasContent) {
  hasSignature.value = hasContent
}

async function handleFinish() {
  if (!canFinish.value) return

  saving.value = true

  try {
    // Get signature blob
    const blob = await signaturePadRef.value.getBlob()

    // Resolve agent number: for admins, read from wallet metadata
    let agentNumber = authStore.userAgentId || ''
    if (!agentNumber && authStore.isAdmin) {
      agentNumber = (await getWalletAgentId(authStore.userId)) || ''
    }

    // Confirm payment (wallet + email)
    const result = await confirmPayment(
      authStore.userId,
      {
        name: authStore.userDisplayName || '',
        number: agentNumber,
      },
      props.allocation,
      blob
    )

    toastSuccess(`תשלום נשמר בהצלחה · אישור ${result.approvalNum}`)

    // Reset gvia state and navigate to agent portal
    reset()
    router.push({ name: ROUTES.AGENTS_PORTAL })
  } catch (err) {
    toastError(err.message || 'שגיאה בשמירת התשלום')
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="signature-modal">
      <div class="signature-overlay">
        <div class="signature-container">
          <!-- Header -->
          <div class="signature-header">
            <button class="back-btn" :disabled="saving" @click="emit('close')">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <h2 class="signature-title">חתימת הלקוח</h2>
          </div>

          <!-- Payment Summary -->
          <div class="payment-summary">
            <div class="summary-client">
              <span class="client-name">{{ clientData?.clientName }}</span>
              <span class="client-num">{{ clientData?.clientNum }}</span>
            </div>
            <div class="summary-amount">
              <span class="amount-label">סה"כ</span>
              <span class="amount-value">{{ formatCurrency(allocation.amount) }}</span>
            </div>
            <div class="summary-breakdown">
              <div v-if="hasCash" class="breakdown-item">
                <span class="breakdown-icon cash-dot"></span>
                <span>מזומן: {{ formatCurrency(allocation.cash) }}</span>
              </div>
              <div v-if="hasChecks" class="breakdown-item">
                <span class="breakdown-icon checks-dot"></span>
                <span>שיקים: {{ formatCurrency(allocation.checks) }}</span>
                <span class="checks-count">({{ allocation.checksDetails?.length || 0 }})</span>
              </div>
            </div>
          </div>

          <!-- Signature Area -->
          <div class="signature-area">
            <p class="signature-instruction">חתימת הלקוח לאישור התשלום</p>
            <SignaturePad
              ref="signaturePadRef"
              :width="600"
              :height="200"
              :stroke-width="2.5"
              @update:has-content="handleSignatureUpdate"
            />
          </div>

          <!-- Footer -->
          <div class="signature-footer">
            <button
              class="finish-btn"
              :class="{ active: canFinish }"
              :disabled="!canFinish"
              @click="handleFinish"
            >
              <template v-if="saving">
                <span class="spinner"></span>
                שומר תשלום...
              </template>
              <template v-else>
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                סיים
              </template>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.signature-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-primary);
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 10002;
}

.signature-container {
  background: var(--bg-primary);
  width: 100%;
  max-width: 900px;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

/* Header */
.signature-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--bg-secondary);
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.back-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.back-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.signature-title {
  flex: 1;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* Payment Summary */
.payment-summary {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.summary-client {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.client-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.client-num {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.summary-amount {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.amount-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.amount-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.summary-breakdown {
  display: flex;
  gap: var(--spacing-lg);
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.breakdown-icon {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cash-dot {
  background: var(--color-success);
}

.checks-dot {
  background: var(--color-warning);
}

.checks-count {
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

/* Signature Area */
.signature-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  gap: var(--spacing-md);
  min-height: 0;
}

.signature-instruction {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* Footer */
.signature-footer {
  display: flex;
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.finish-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-md);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-lg);
  color: var(--text-muted);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family);
  cursor: not-allowed;
  transition: all var(--transition-fast);
}

.finish-btn.active {
  background: var(--color-success);
  color: white;
  cursor: pointer;
}

.finish-btn.active:hover {
  background: #0ea572;
}

.finish-btn.active:active {
  transform: scale(0.98);
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transition */
.signature-modal-enter-active,
.signature-modal-leave-active {
  transition: opacity 0.25s ease;
}

.signature-modal-enter-from,
.signature-modal-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .signature-header {
    padding: var(--spacing-md);
  }

  .payment-summary {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .signature-area {
    padding: var(--spacing-md);
  }

  .signature-footer {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .summary-breakdown {
    flex-direction: column;
    gap: var(--spacing-xs);
  }
}
</style>
